import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingState } from "../../components/states";
import { useToast } from "../../components/toast/toastContext";
import { NumberField, SettingForm, SettingPanel } from "../../components/admin/SettingPanel";
import { fetchDormancyThresholdSetting, updateDormancyThresholdSetting } from "../../api/writeSafety";

const SettingsWritePermission = "settings.write";

/** The dormancy threshold (`PD-13`): how many days without observed usage before a person counts as dormant. */
export function DormancyThresholdSection(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; thresholdDays: number } | { kind: "error"; message: string }>({ kind: "loading" });
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchDormancyThresholdSetting();
    if (result.kind === "ok") {
      setState({ kind: "loaded", thresholdDays: result.response.thresholdDays });
      setDraft(String(result.response.thresholdDays));
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.writeSafety.denied.description") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setPending(true);
    const result = await updateDormancyThresholdSetting(Number(draft));
    setPending(false);
    if (result.kind === "ok") {
      setState({ kind: "loaded", thresholdDays: result.response.thresholdDays });
      toast.show(t("admin.writeSafety.saved"), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  return (
    <SettingPanel id="dormancy-threshold" title={t("admin.writeSafety.dormancy.title")} intro={t("admin.writeSafety.dormancy.explanation")}>
      {state.kind === "loading" && <LoadingState label={t("admin.writeSafety.dormancy.loading")} />}
      {state.kind === "error" && <ErrorState description={state.message} onRetry={() => void load()} />}
      {state.kind === "loaded" && (
        <SettingForm>
          <NumberField id="dormancy-threshold-days" label={t("admin.writeSafety.dormancy.thresholdDays")} min={1} value={draft} onChange={setDraft} />
          <button type="button" className="button button-secondary" disabled={pending} onClick={() => void handleSave()}>
            {t("admin.writeSafety.controls.save")}
          </button>
        </SettingForm>
      )}
    </SettingPanel>
  );
}
